// A board card's link to a company priority — the picker's contents and the
// board's expectation, stated once.
//
// ── THE CASCADE THIS COMPLETES (Brandon's whiteboard, 2026-09-15) ──────────
// C-Suite card → company priority → the priority's task list with deadlines →
// each assignee's My Day, ranked by the priority. Everything downstream of the
// link already exists in the Priority Engine; this module only decides what
// the picker shows and when a board asks for the link.
//
// Pure. No I/O.

export const PRIORITY_LINK_VERSION = "priority-link-v0.1";

export interface LinkableObjective {
  id: string;
  title: string;
  priority: number;        // 1..5
  deadline: string;        // YYYY-MM-DD
  status: string;
}

/**
 * What the picker offers: active priorities only, most important first, then
 * nearest deadline — the same order Company Priorities shows them, so the
 * picker reads like the page everyone already knows.
 */
export function linkableObjectives(objectives: LinkableObjective[]): LinkableObjective[] {
  return objectives
    .filter(o => o.status === "active")
    .sort((a, b) => (a.priority - b.priority) || a.deadline.localeCompare(b.deadline));
}

/** One line per option: the rank first, because the rank is why you pick it. */
export function objectiveOptionLabel(o: LinkableObjective): string {
  const d = new Date(`${o.deadline}T00:00:00`);
  const due = Number.isNaN(d.getTime())
    ? o.deadline
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return `P${o.priority} · ${o.title} — due ${due}`;
}

/**
 * Does this card still owe its board a priority link?
 *
 * A required link is a nudge, not a lock: quick capture must stay quick, so
 * the card saves either way and wears this until somebody links it.
 */
export function needsPriorityLink(
  board: { requires_priority_link?: boolean | null } | null | undefined,
  task: { pe_objective_id?: string | null },
): boolean {
  return !!board?.requires_priority_link && !task.pe_objective_id;
}
